import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams  } from "react-router-dom";
import axios from "axios";
import { useTranslation, Trans } from "react-i18next";
//import { useData } from "../contexts/DataContext";
import { useAppData } from "../hooks/useAppData";
import { useAppStore } from "../hooks/useAppStore";
import { useUserStore } from "../hooks/useUserStore"; 

import LogRocket from "logrocket";
import * as Sentry from "@sentry/react";

import { 
  Button 
} from '@mui/material';

import BwossyLogo from "../components/Layout/BwossyLogo"; 

const VerifyResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email"); // Extract email from URL

  const [status, setStatus] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const hasVerified = useRef(false); // Prevent multiple calls

  /*const { setWordListId, setSoundboardId } = useData();*/ 
  const { 
      wordListId, setWordListId, 
      soundboardId, setSoundboardId,
      stageWidth, setStageWidth,
      stageHeight, setStageHeight,
      buttonPopovers, setButtonPopovers,
      shapePopovers, setShapePopovers,
      selectedItem, setSelectedItem,
      wordListWidth, setWordListWidth
  } = useAppStore();

  const { 
    user, setUser,
    userToken, setUserToken,
} = useUserStore();

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const handleSuccessNavigation = async () => {
    try {
      const token = searchParams.get("token");
      if (!token) {
        navigate("/");
        return;
      }

      const response = await axios.get(`${baseURL}/api/users/getCurrentUserByVerifiedToken`, {
        headers: { Authorization: `Bearer ${token}` }, // 🔹 Pass JWT in headers
      });

      if(response && response.data && response.data.accessToken) {
        const { user, accessToken, wordListId, soundboardId } = response.data;

        if (!user.email) {
          navigate("/"); // Redirect to login if no credentials are available
          return;
        }

        localStorage.setItem('userToken', accessToken);
        setUserToken(accessToken);
        setUser(user);
        setWordListId(wordListId);
        setSoundboardId(soundboardId);

        LogRocket.identify(user.id, {
          email: user.email,
          loginMethod: 'Form', 
        });

        Sentry.setUser({
          id: user.id,
          email: user.email,
        });
        
        if (user.setupProgress) {
          navigate('/setup-wizard/0');
        } else {
          navigate('/MainContent');
        }

      } else {
        navigate("/"); // Redirect to login if login fails
      }
    } catch (error) {
      navigate("/");
    }
  };


  const handleResendEmail = async () => {
    if (emailSent || !email) return; // Prevent multiple clicks & missing email

    setLoading(true);
    setMessage("");

    try {
      // Get user consent for email tracking
      let consentTracking = false;
      if (window.klaro && typeof window.klaro.getManager === "function") {
        const manager = window.klaro.getManager();
        consentTracking = manager.getConsent("brevo") || false;
      }

      const response = await axios.post(`${baseURL}/api/users/resendVerificationEmail`, {
        email,
        consentTracking,
      });

      setMessage(response.data.message);
      setEmailSent(true);
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to resend email.");
    } finally {
      setLoading(false);
    }
  };

  const statusMessages = {
    loading: { title: "", text: t("verifyResult.loading") },
    success: { 
      title: t("verifyResult.successTitle"), 
      text: t("verifyResult.successText"), 
      buttonText: t("verifyResult.callToAction"), 
      buttonAction: handleSuccessNavigation,
    },
    alreadyVerified: { 
      title: t("verifyResult.alreadyVerifiedTitle"), 
      text: t("verifyResult.alreadyVerifiedText"), 
      buttonText: t("verifyResult.callToAction"), 
      buttonAction: handleSuccessNavigation,
    },
    expired: { 
      title: t("verifyResult.expiredTitle"), 
      text: message || t("verifyResult.expiredText"),
      buttonText: emailSent ? t("verifyResult.emailSent") : t("verifyResult.resendVerificationEmail"),
      buttonAction: handleResendEmail,
      disabled: emailSent || loading, 
    },
    failed: { 
      title: t("verifyResult.failedTitle"), 
      text: t("verifyResult.failedText"), 
      buttonText: t("verifyResult.failedRedirect"), 
      buttonAction: () => navigate("/"),
    }
  };

  useEffect(() => {
    if (hasVerified.current) return; // Stop re-execution
    hasVerified.current = true; // Mark as executed

    const verifyEmail = async () => {
      const token = searchParams.get("token");
      const email = searchParams.get("email");

      if (!token) {
          setStatus("failed");
          return;
        }

        try {
          const response = await axios.post(`${baseURL}/api/users/verifyEmail`, { token, email });

          if (response.data.success) {
            setStatus("success");
            localStorage.setItem("verifiedEmail", response.data.email); // Store email for auto-login
          } else if (response.data.alreadyVerified) {
            setStatus("alreadyVerified");
          } else {
            setStatus("failed");
          }
        } catch (error) {
          if (error.response?.data?.message === "Verification link expired") {
            setStatus("expired"); 
          } else {
            setStatus("failed");
          }
        }
    }

    verifyEmail();
  }, []);

  return (
    <div className="verify-container">
      {status === "loading" ? (
        <p>{statusMessages.loading.text}</p>
      ) : (
        <>
        <div className="login-logo-container">
            <a href="./">
              <span className="login-logo-name">
                <Trans i18nKey="common.appNameLogo" components={[<BwossyLogo />]} />
              </span>
            </a>
          </div>
          <div className="verify-content-container">

            <h2>{statusMessages[status]?.title}</h2>
            <p>{statusMessages[status]?.text}</p>
            {statusMessages[status]?.buttonText && (

              <Button
              type="submit"
              disabled={statusMessages[status]?.disabled}
              onClick={statusMessages[status].buttonAction}
              variant="contained"
              >
                {statusMessages[status].buttonText}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VerifyResult;